import { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { userSelector } from "./user-redux/selector";
import { selectedJobAction } from "./redux/action";
import Key from "../clientVariables.json";
import "./styles/ChatBot.css";

const INITIAL_GREETING = {
    role: "ai",
    content:
        "Hello! I am your AI assistant (**Beta**). How can I help you today?",
};

/** Public / legal pages where the floating AI chat should not appear */
const CHATBOT_HIDDEN_PATHS = [
    "/ai-terms-of-use",
    "/privacy-policy",
    "/terms-and-conditions",
    "/login",
    "/home",
    "/about-us",
    "/blog",
];

function isChatBotHiddenPath(pathname) {
    const path = String(pathname || "").toLowerCase();
    if (path === "/" || path === "") return false;
    return CHATBOT_HIDDEN_PATHS.some(
        (p) => path === p || path.startsWith(`${p}/`)
    );
}

function RobotFaceIcon({
    size = 68,
    eyeOffset = { x: 0, y: 0 },
    idPrefix = "bot",
    className = "bot-svg",
}) {
    const shell = `${idPrefix}-shell`;
    const outline = `${idPrefix}-outline`;
    const face = `${idPrefix}-face`;
    const eye = `${idPrefix}-eye`;
    const scan = `${idPrefix}-scan`;
    const glow = `${idPrefix}-glow`;

    return (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 68 68"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id={shell} x1="8" y1="20" x2="60" y2="48">
                    <stop offset="0%" stopColor="#ffe4d1" />
                    <stop offset="45%" stopColor="#f5f5f7" />
                    <stop offset="100%" stopColor="#d7e0f5" />
                </linearGradient>
                <filter id={outline} x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow
                        dx="0"
                        dy="1"
                        stdDeviation="0.4"
                        floodColor="#0f2744"
                        floodOpacity="0.35"
                    />
                </filter>
                <linearGradient id={face} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1e3a5f" />
                    <stop offset="100%" stopColor="#0f2744" />
                </linearGradient>
                <linearGradient id={eye} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5eead4" />
                    <stop offset="100%" stopColor="#22d3ee" />
                </linearGradient>
                <pattern
                    id={scan}
                    patternUnits="userSpaceOnUse"
                    width="40"
                    height="4"
                >
                    <rect width="40" height="2" fill="#1a4a7a" opacity="0.35" />
                </pattern>
                <filter id={glow} x="-40%" y="-40%" width="180%" height="180%">
                    <feGaussianBlur stdDeviation="0.8" result="b" />
                    <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            <circle cx="34" cy="11" r="3.2" fill="#1e3a5f" />
            <line
                x1="34"
                y1="7.5"
                x2="34"
                y2="3"
                stroke="#1e3a5f"
                strokeWidth="1.6"
                strokeLinecap="round"
            />
            <circle cx="34" cy="2.2" r="2" fill="#1e3a5f" />

            <ellipse cx="9.5" cy="36" rx="5.5" ry="7.5" fill="#1e3a5f" />
            <ellipse cx="58.5" cy="36" rx="5.5" ry="7.5" fill="#1e3a5f" />

            <rect
                x="10"
                y="12"
                width="48"
                height="46"
                rx="18"
                fill={`url(#${shell})`}
                stroke="#1e3a5f"
                strokeWidth="2"
                filter={`url(#${outline})`}
            />

            <line
                x1="34"
                y1="14"
                x2="34"
                y2="22"
                stroke="#1e3a5f"
                strokeWidth="1.4"
                strokeLinecap="round"
                opacity="0.85"
            />

            <path
                d="M18 26 C18 22 20 20 24 20 H44 C48 20 50 22 50 26 V46 C50 50 47 52.5 43 52.5 H25 C21 52.5 18 50 18 46 Z"
                fill={`url(#${face})`}
            />
            <path
                d="M18 26 C18 22 20 20 24 20 H44 C48 20 50 22 50 26 V46 C50 50 47 52.5 43 52.5 H25 C21 52.5 18 50 18 46 Z"
                fill={`url(#${scan})`}
            />

            <g filter={`url(#${glow})`}>
                <ellipse
                    cx={26.5 + eyeOffset.x}
                    cy={34 + eyeOffset.y}
                    rx="3.4"
                    ry="5.2"
                    fill={`url(#${eye})`}
                />
                <ellipse
                    cx={41.5 + eyeOffset.x}
                    cy={34 + eyeOffset.y}
                    rx="3.4"
                    ry="5.2"
                    fill={`url(#${eye})`}
                />
                <path
                    d="M29.5 44c1.4 1.6 2.9 2.2 4.5 2.2s3.1-.6 4.5-2.2"
                    stroke="#22d3ee"
                    strokeWidth="2"
                    strokeLinecap="round"
                    fill="none"
                />
            </g>
        </svg>
    );
}

function ChatBot() {
    const { user } = useSelector(userSelector);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    console.log("user", user);
    const isLoggedIn =
        user != null && Object.keys(user).length > 0 && Boolean(user?.id);
    const hideOnThisPage = isChatBotHiddenPath(location.pathname);

    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [question, setQuestion] = useState("");
    const [chatHistory, setChatHistory] = useState([INITIAL_GREETING]);
    const [isLoading, setIsLoading] = useState(false);
    const [eyeOffset, setEyeOffset] = useState({ x: 0, y: 0 });
    const [showTip, setShowTip] = useState(false);
    const [tipDismissed, setTipDismissed] = useState(false);
    const chatEndRef = useRef(null);
    const robotRef = useRef(null);
    const tipHideTimerRef = useRef(null);
    const prevUserIdRef = useRef(null);
    const lastTopicRef = useRef(null);

    const resetChat = useCallback(() => {
        setIsOpen(false);
        setIsExpanded(false);
        setShowSuggestions(false);
        setQuestion("");
        setChatHistory([INITIAL_GREETING]);
        setIsLoading(false);
        lastTopicRef.current = null;
    }, []);

    /** Clear conversation but keep the panel open */
    const refreshChat = useCallback(() => {
        setShowSuggestions(false);
        setQuestion("");
        setChatHistory([INITIAL_GREETING]);
        setIsLoading(false);
        lastTopicRef.current = null;
    }, []);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [chatHistory, isLoading, isOpen]);

    useEffect(() => {
        const handleOpenChat = () => setIsOpen(true);
        window.addEventListener("openChatBot", handleOpenChat);
        return () => window.removeEventListener("openChatBot", handleOpenChat);
    }, []);

    // Robot eyes follow the mouse pointer (float + header icon)
    useEffect(() => {
        const MAX = 2.2;
        const onMove = (e) => {
            const el = robotRef.current;
            // Prefer header icon when chat is open
            const target =
                (isOpen &&
                    document.querySelector(".bot-svg--header")) ||
                el;
            if (!target) return;
            const rect = target.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height * 0.52;
            const dx = e.clientX - cx;
            const dy = e.clientY - cy;
            const dist = Math.hypot(dx, dy) || 1;
            const scale = Math.min(dist / 100, 1);
            setEyeOffset({
                x: (dx / dist) * MAX * scale,
                y: (dy / dist) * MAX * scale,
            });
        };

        window.addEventListener("mousemove", onMove, { passive: true });
        return () => window.removeEventListener("mousemove", onMove);
    }, [isOpen]);

    // Logout or switch user → start a new chat
    useEffect(() => {
        const currentUserId = user?.id ?? null;

        if (!isLoggedIn) {
            if (prevUserIdRef.current != null) {
                resetChat();
            }
            prevUserIdRef.current = null;
            return;
        }

        if (
            prevUserIdRef.current != null &&
            prevUserIdRef.current !== currentUserId
        ) {
            resetChat();
        }

        prevUserIdRef.current = currentUserId;
    }, [isLoggedIn, user?.id, resetChat]);

    const askQuestion = async (e, overriddenQuestion = null) => {
        e.preventDefault();
        const currentQuestion = overriddenQuestion || question;
        if (!currentQuestion.trim()) return;

        const requestStartedAt = Date.now();
        console.log("[ChatBot] Sending question", {
            question: currentQuestion,
            userId: user?.id,
            userType: user?.usertype,
            endpoint: `${Key.domain}/common/chat`,
        });

        const userMessage = { role: "user", content: currentQuestion };
        setChatHistory((prev) => [...prev, userMessage]);
        setQuestion("");
        setIsLoading(true);

        try {
            const res = await fetch(`${Key.domain}/common/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    question: currentQuestion,
                    user_id: user.id,
                    ca_id: user.ca_id,
                    admin_id: user.admin_id,
                    companyid: user.companyid,
                    full_name: user.full_name || user.name,
                    usertype: user.usertype,
                    candidate_id: user.candidate_id || null,
                    last_topic: lastTopicRef.current,
                }),
            });

            const data = await res.json();
            console.log("[ChatBot] Chat response", {
                status: res.status,
                ok: res.ok,
                elapsedMs: Date.now() - requestStartedAt,
                data,
            });

            if (res.ok) {
                if (data.topic) {
                    lastTopicRef.current = data.topic;
                }
                setChatHistory((prev) => [
                    ...prev,
                    {
                        role: "ai",
                        content: data.answer,
                        jobs: Array.isArray(data.jobs)
                            ? data.jobs
                            : Array.isArray(data.topic?.jobs)
                              ? data.topic.jobs
                              : null,
                    },
                ]);
            } else {
                setChatHistory((prev) => [
                    ...prev,
                    {
                        role: "ai",
                        content:
                            "**Error:** " + (data.error || "Could not connect to service."),
                    },
                ]);
            }
        } catch (error) {
            console.error("[ChatBot] Chat request failed", {
                elapsedMs: Date.now() - requestStartedAt,
                error,
            });
            setChatHistory((prev) => [
                ...prev,
                { role: "ai", content: "**Server connection failed.**" },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const openJobFromChat = useCallback(
        (jobOrId) => {
            const job =
                jobOrId && typeof jobOrId === "object"
                    ? jobOrId
                    : { id: jobOrId };
            if (!job?.id) return;
            dispatch(selectedJobAction(job));
            setIsOpen(false);
            navigate("/candidates-list", {
                state: { selectedTab: 0, selectedJobId: job.id },
            });
        },
        [dispatch, navigate]
    );

    const markdownComponents = {
        a: ({ href, children, ...props }) => {
            if (href && String(href).startsWith("jnp-job:")) {
                const jobId = String(href).replace("jnp-job:", "").trim();
                return (
                    <button
                        type="button"
                        className="chat-job-md-link"
                        onClick={(e) => {
                            e.preventDefault();
                            openJobFromChat(jobId);
                        }}
                    >
                        {children}
                    </button>
                );
            }
            return (
                <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                    {children}
                </a>
            );
        },
    };

    const clearTipHideTimer = () => {
        if (tipHideTimerRef.current) {
            clearTimeout(tipHideTimerRef.current);
            tipHideTimerRef.current = null;
        }
    };

    const openTip = () => {
        if (isOpen || tipDismissed) return;
        clearTipHideTimer();
        setShowTip(true);
    };

    const scheduleHideTip = () => {
        clearTipHideTimer();
        tipHideTimerRef.current = setTimeout(() => setShowTip(false), 180);
    };

    const dismissTip = (e) => {
        e.stopPropagation();
        e.preventDefault();
        clearTipHideTimer();
        setTipDismissed(true);
        setShowTip(false);
    };

    useEffect(() => {
        if (isOpen) setShowTip(false);
    }, [isOpen]);

    useEffect(() => {
        return () => clearTipHideTimer();
    }, []);

    useEffect(() => {
        if (hideOnThisPage) {
            setIsOpen(false);
            setShowTip(false);
        }
    }, [hideOnThisPage]);

    if (!isLoggedIn || hideOnThisPage) {
        return null;
    }

    const tipText =
        Number(user?.usertype) === 13
            ? "Need help managing your bench? Ask me about candidates, recruiters, submissions, interviews, or available jobs."
            : Number(user?.usertype) === 3
              ? "Need help with your bench? Ask me about your candidates, submissions, interviews, or today's jobs."
              : "Looking for your next role? Ask me about available jobs, submissions, interviews, or your tasks.";

    return (
        <div className="chatbot-wrapper">
            {!isOpen && showTip && (
                <div
                    className="chatbot-hover-tip"
                    role="status"
                    onMouseEnter={openTip}
                    onMouseLeave={scheduleHideTip}
                >
                    <button
                        type="button"
                        className="chatbot-hover-tip-close"
                        onClick={dismissTip}
                        aria-label="Dismiss tip"
                    >
                        ×
                    </button>
                    <p>{tipText}</p>
                </div>
            )}

            <button
                type="button"
                ref={robotRef}
                className={`widget-toggle robot-toggle ${isOpen ? "hidden" : ""}`}
                onClick={() => setIsOpen(true)}
                onMouseEnter={openTip}
                onMouseLeave={scheduleHideTip}
                onFocus={openTip}
                onBlur={scheduleHideTip}
                aria-label="Open AI assistant (Beta)"
                title="AI Assistant (Beta)"
            >
                <RobotFaceIcon
                    size={68}
                    eyeOffset={eyeOffset}
                    idPrefix="bot-float"
                />
                <span className="chatbot-beta-float" aria-hidden="true">
                    Beta
                </span>
            </button>

            <div
                className={`app-container ${!isOpen ? "closed" : ""} ${
                    isExpanded ? "expanded" : ""
                }`}
            >
                <header className="header">
                    <div className="header-title">
                        <RobotFaceIcon
                            size={40}
                            eyeOffset={eyeOffset}
                            idPrefix="bot-header"
                            className="bot-svg bot-svg--header"
                        />
                        <div className="header-title-text">
                            <h2>Jobs'n'Profiles AI Assistant</h2>
                            <span className="chatbot-beta-badge" title="Beta version">
                                Beta
                            </span>
                        </div>
                    </div>
                    <div className="header-actions">
                        <button
                            type="button"
                            className="header-btn header-btn--refresh"
                            onClick={refreshChat}
                            disabled={isLoading}
                            aria-label="Refresh chat"
                            title="Refresh chat"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <polyline points="23 4 23 10 17 10" />
                                <polyline points="1 20 1 14 7 14" />
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={`header-btn header-btn--resize ${
                                isExpanded ? "is-expanded" : ""
                            }`}
                            onClick={() => setIsExpanded((v) => !v)}
                            aria-label={isExpanded ? "Collapse chat width" : "Expand chat width"}
                            title={isExpanded ? "Collapse width" : "Expand width"}
                        >
                            <svg
                                className="resize-icon"
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                {/* vertical grip dots */}
                                <circle cx="12" cy="3.5" r="1.7" />
                                <circle cx="12" cy="8" r="1.7" />
                                <circle cx="12" cy="12.5" r="1.7" />
                                <circle cx="12" cy="17" r="1.7" />
                                <circle cx="12" cy="21.5" r="1.7" />
                                {/* left arrow */}
                                <path
                                    d="M9 12.5H3.5M3.5 12.5l2.4-2.4M3.5 12.5l2.4 2.4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                {/* right arrow */}
                                <path
                                    d="M15 12.5h5.5M20.5 12.5l-2.4-2.4M20.5 12.5l-2.4 2.4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className="header-btn header-btn--minimize"
                            onClick={() => setIsOpen(false)}
                            aria-label="Minimize chat"
                            title="Minimize"
                        >
                            <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </button>
                    </div>
                </header>

                <div className="chat-window">
                    {chatHistory.map((msg, index) => {
                        const hasJobCards =
                            Array.isArray(msg.jobs) && msg.jobs.length > 0;
                        const displayContent = hasJobCards
                            ? String(msg.content || "")
                                  .split("\n")
                                  .filter(
                                      (line) =>
                                          !line.includes("jnp-job:") &&
                                          !/^\d+\.\s+\*\*/.test(line.trim())
                                  )
                                  .join("\n")
                                  .trim()
                            : msg.content;

                        return (
                        <div key={index} className={`message ${msg.role}`}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={markdownComponents}
                            >
                                {displayContent}
                            </ReactMarkdown>
                            {hasJobCards && (
                                <div className="chat-job-list">
                                    {msg.jobs.map((job, jobIndex) => {
                                        const title =
                                            job.jobtitle ||
                                            job.title ||
                                            `Job #${job.id}`;
                                        const company = job.company_name || "";
                                        const loc = job.location
                                            ? job.location
                                            : parseInt(job.remote, 10) === 1
                                              ? "Remote"
                                              : "";
                                        return (
                                            <div
                                                key={job.id || jobIndex}
                                                className="chat-job-item"
                                            >
                                                <div className="chat-job-info">
                                                    <div className="chat-job-title">
                                                        {jobIndex + 1}. {title}
                                                    </div>
                                                    {(company || loc) && (
                                                        <div className="chat-job-meta">
                                                            {[company, loc]
                                                                .filter(Boolean)
                                                                .join(" · ")}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="chat-job-actions">
                                                    <button
                                                        type="button"
                                                        className="chat-job-btn chat-job-btn--view"
                                                        title="View job"
                                                        onClick={() =>
                                                            openJobFromChat(job)
                                                        }
                                                    >
                                                        <svg
                                                            width="15"
                                                            height="15"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2.2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            aria-hidden="true"
                                                        >
                                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                                                            <circle
                                                                cx="12"
                                                                cy="12"
                                                                r="3"
                                                            />
                                                        </svg>
                                                        <span>View job</span>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        );
                    })}
                    {isLoading && (
                        <div className="typing">
                            <span>Thinking</span>
                            <div className="dot"></div>
                            <div className="dot dot--2"></div>
                            <div className="dot dot--3"></div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>

                {!isLoading && (
                    <div className="suggested-questions">
                        {(chatHistory.length === 1 || showSuggestions) &&
                            (Number(user?.usertype) === 13
                                ? [
                                      "How many active candidates do I have?",
                                      "My submissions",
                                      "Make recruiter inactive",
                                      "Activate a candidate",
                                  ]
                                : Number(user?.usertype) === 3
                                  ? [
                                        "How many active candidates do I have?",
                                        "My submissions today",
                                        "My interviews this week",
                                        "Activate a candidate",
                                    ]
                                  : [
                                        "Jobs available today",
                                        "Here are top 10 jobs",
                                        "Add task Update my resume",
                                        "Important tasks",
                                    ]
                            ).map((q, i) => (
                                <button
                                    key={i}
                                    className="suggestion-pill"
                                    onClick={() => {
                                        setShowSuggestions(false);
                                        askQuestion({ preventDefault: () => {} }, q);
                                    }}
                                >
                                    {q}
                                </button>
                            ))}
                        {chatHistory.length > 1 && !showSuggestions && (
                            <button
                                className="suggestions-toggle"
                                onClick={() => setShowSuggestions(true)}
                                aria-label="Show suggested questions"
                                title="Suggested questions"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M9 18h6" />
                                    <path d="M10 22h4" />
                                    <path d="M15.1 14c.6-.6 1.4-1.5 1.4-3a4.5 4.5 0 0 0-9 0c0 1.5.8 2.4 1.4 3 .6.6 1.1 1.2 1.1 2h4c0-.8.5-1.4 1.1-2Z" />
                                </svg>
                            </button>
                        )}
                    </div>
                )}

                <div className="input-area">
                    <form onSubmit={askQuestion} className="input-container">
                        <input
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="Ask me anything..."
                            disabled={isLoading}
                        />
                        <button
                            type="submit"
                            disabled={isLoading || !question.trim()}
                        >
                            {isLoading ? "..." : "Send"}
                        </button>
                    </form>
                    <p className="chatbot-ai-terms">
                        By using this assistant you agree to our{" "}
                        <a
                            href="/ai-terms-of-use"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            AI Terms of Use
                        </a>
                        .
                    </p>
                </div>
            </div>
        </div>
    );
}

export default ChatBot;
